// v6 — retroactive repair details modal added (admin only, inside Edit Tool modal)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../components/AuthContext';
import MobileTagout from './MobileTagout';
import { sendNotification, getAdminIds, getBranchManagerEmails, getAdminEmails, sendEmail, emailToolTaggedOut } from '../lib/notify';
import { resolveToolValue, ACQUISITION_TYPES } from '../lib/utils';
import InlineMessage from '../components/InlineMessage';
import useMessage from '../lib/useMessage';
import { uploadPhoto, getPhotoUrl } from '../lib/storage';
import OnboardingCallout from '../components/OnboardingCallout';
import { hasSeenCallout } from '../lib/userPreferences';
import LabelCard from '../components/inventory/LabelCard';
import BatchPrintModal from '../components/inventory/BatchPrintModal';
import { DESTINATIONS, LABEL_FORMATS } from '../lib/labelFormats';

const STATUS_BADGE = {
  active: 'badge-active', tagged_out: 'badge-tagged', in_repair: 'badge-repair',
  in_transit: 'badge-transit', retired: 'badge-retired', spare: 'badge-spare',
  pending: 'badge-pending', available: 'badge-spare',
};
const STATUS_LABEL = {
  active: 'Active', tagged_out: 'Tagged out', in_repair: 'In repair',
  in_transit: 'In transit', retired: 'Retired', spare: 'Spare',
  pending: 'Pending receipt', available: 'Available',
};
const EVENT_LABEL = {
  added: 'Added to inventory', status_change: 'Status changed',
  tagged_out: 'Tagged out', receipt_confirmed: 'Receipt confirmed',
  edited: 'Record edited', spare_received: 'Received from Dallas spare pool',
};

function fmt$(v) { return v != null ? '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'; }
function fmtDate(ts) { return ts ? new Date(ts).toLocaleString() : '—'; }

async function logHistory(toolId, eventType, oldStatus, newStatus, changedByName, changedById, notes, metadata) {
  await supabase.from('tool_history').insert({
    tool_id: toolId, event_type: eventType, old_status: oldStatus || null,
    new_status: newStatus || null, changed_by_name: changedByName || null,
    changed_by_id: changedById || null, notes: notes || null, metadata: metadata || null,
  });
}

function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggle = (val) => {
    onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
  };

  const displayLabel = selected.length === 0
    ? label
    : selected.length === 1
      ? options.find(o => o.value === selected[0])?.label || selected[0]
      : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="filter-select"
        onClick={() => setOpen(v => !v)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', cursor: 'pointer', background: 'white', minWidth: '140px' }}
      >
        <span style={{ color: selected.length === 0 ? 'var(--gray-mid)' : 'var(--black)' }}>{displayLabel}</span>
        <span style={{ fontSize: '10px', color: 'var(--gray-lt)' }}>▼</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.10)', minWidth: '180px', padding: '4px 0',
        }}>
          {selected.length > 0 && (
            <button onClick={() => onChange([])} style={{ width: '100%', textAlign: 'left', padding: '8px 12px', fontSize: '12px', color: 'var(--green-dk)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
              Clear all
            </button>
          )}
          {options.map(o => (
            <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', cursor: 'pointer', fontSize: '13px', color: 'var(--black)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--off-white)'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} style={{ accentColor: 'var(--green-dk)' }} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MOBILE TOOL CARD ──────────────────────────────────────
function ToolCard({ tool, onTagout, onHistory, isManager, isAdmin, isTeamMember }) {
  const canTagout = tool.status === 'active';
  const isOut = ['tagged_out', 'in_repair', 'in_transit'].includes(tool.status);

  return (
    <div className="tool-card" style={{ borderLeft: `4px solid ${isOut ? '#F5A623' : tool.status === 'pending' ? '#1976D2' : '#E8E8E8'}` }}>
      <div className="tool-card-top">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tool-card-name">{tool.name}</div>
          {(tool.model_number || tool.serial_number) && (
            <div className="tool-card-model">
              {[tool.model_number, tool.serial_number ? 'S/N: ' + tool.serial_number : null].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <span className={`badge ${STATUS_BADGE[tool.status] || 'badge-active'}`} style={{ flexShrink: 0 }}>
          {STATUS_LABEL[tool.status] || tool.status}
        </span>
      </div>
      <div className="tool-card-meta">
        {tool.branches?.name && <span className="tool-card-chip">{tool.branches.name}</span>}
        {tool.department && <span className="tool-card-chip">{tool.department}</span>}
        {tool.station_truck && <span className="tool-card-chip">Station {tool.station_truck}</span>}
      </div>
      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
        <button className="btn btn-ghost btn-sm" onClick={() => onHistory(tool)} style={{ flex: 1 }}>History</button>
        <LabelCard mode="tool" variant="sheetTrigger" isMobile={true} item={tool} branchName={tool.branches?.name || ''} />
        {canTagout && (
          <button className="btn btn-danger btn-sm" onClick={() => onTagout(tool)} style={{ flex: 1 }}>⚠ Tag out</button>
        )}
      </div>
    </div>
  );
}

// ── FLEET PAGE ────────────────────────────────────────────
export default function FleetPage() {
  // labelPrintingEnabled -- #199 part 3: with QR label printing off, the
  // batch Print N labels button goes too (the per-row icons are LabelCard's).
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, companyName, labelPrintingEnabled } = useAuth();
  const [tools, setTools] = useState([]);
  const [branches, setBranches] = useState([]);
  // Session 76. The batch button used to print straight to one hardcoded
  // Brother 2x1 and never touched the printer registry, so a branch with a
  // Zebra could not reach it and nobody could choose the 1.5x1 tool rung.
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState([]);
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterDept, setFilterDept] = useState([]);
  const [filterStation, setFilterStation] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showTagout, setShowTagout] = useState(false);
  const [showReceive, setShowReceive] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [historyTool, setHistoryTool] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editTool, setEditTool] = useState(null);
  const [tagoutTool, setTagoutTool] = useState(null);
  const [receiveTool, setReceiveTool] = useState(null);
  const [form, setForm] = useState({});
  const [tagForm, setTagForm] = useState({});
  const [receiveForm, setReceiveForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const PS = 15;
  const isMobile = window.innerWidth <= 768;
  const [showMobileTagout, setShowMobileTagout] = useState(false);
  const [mobileTagoutTool, setMobileTagoutTool] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // ── Retroactive repair details state ──
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairTool, setRepairTool] = useState(null);
  const [tagoutHistory, setTagoutHistory] = useState([]);
  const [tagoutHistoryLoading, setTagoutHistoryLoading] = useState(false);
  const [selectedTagout, setSelectedTagout] = useState(null);
  const [repairForm, setRepairForm] = useState({});
  const [repairSaving, setRepairSaving] = useState(false);
  const [repairReceiptFile, setRepairReceiptFile] = useState(null);
  const [repairReceiptPreview, setRepairReceiptPreview] = useState(null);
  const [repairReceiptUploading, setRepairReceiptUploading] = useState(false);
  const repairReceiptRef = useRef(null);
  // ── Callout refs ──
const tagoutBtnRef = useRef(null);
const exportCsvBtnRef = useRef(null);
const [tagoutCalloutDismissed, setTagoutCalloutDismissed] = useState(false);

useEffect(() => {
  if (hasSeenCallout(profile, 'fleet_tagout')) setTagoutCalloutDismissed(true);
}, [profile]);

  // ── Per-modal message hooks ──
  const toolMsg    = useMessage();
  const tagoutMsg  = useMessage();
  const receiveMsg = useMessage();
  const toolbarMsg = useMessage();
  const repairMsg  = useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('tools').select('*, branches(name,city), tool_models(msrp)').order('name');
    const branchId = profile?.branch_id ? parseInt(profile.branch_id) : null;
    if (!isAdmin && !isExecutive && branchId) q = q.eq('branch_id', branchId);
    if (isManager && profile?.department) q = q.eq('department', profile.department);
    if (isTeamMember) {
      if (profile?.department) q = q.eq('department', profile.department);
      if (profile?.station_truck) q = q.eq('station_truck', profile.station_truck);
    }
    const { data } = await q;
    setTools(data || []);
    const { data: br } = await supabase.from('branches').select('*').order('name');
    setBranches(br || []);
    setLoading(false);
  }, [isAdmin, isExecutive, isManager, isTeamMember, profile?.branch_id, profile?.department, profile?.station_truck]);

  useEffect(() => { load(); }, [load]);

  const filtered = tools.filter(t => {
    const q = search.toLowerCase();
    const match = !q || [t.name, t.model_number, t.serial_number, t.department, t.station_truck, t.branches?.name].filter(Boolean).join(' ').toLowerCase().includes(q);
    const br = filterBranch.length === 0 || filterBranch.includes(String(t.branch_id));
    const st = filterStatus.length === 0 || filterStatus.includes(t.status);
    const dp = filterDept.length === 0 || filterDept.includes(t.department);
    const stn = filterStation.length === 0 || filterStation.includes(t.station_truck);
    return match && br && st && dp && stn;
  });

  const paged = filtered.slice((page - 1) * PS, page * PS);
  const pages = Math.max(1, Math.ceil(filtered.length / PS));
  // Selection survives filtering, so this reads from `filtered` exactly as the
  // old button's inline argument did — narrowing the filter after selecting
  // narrows what prints, which is the behaviour that was already there.
  const selectedTools = filtered.filter(t => selectedIds.has(t.id));
  const printBranchName = branches.find(b => String(b.id) === String(profile?.branch_id))?.name || '';
  const depts = [...new Set(tools.map(t => t.department).filter(Boolean))].sort();
  const stations = [...new Set(tools.map(t => t.station_truck).filter(Boolean))].sort((a, b) => {
    const an = parseInt(a); const bn = parseInt(b);
    return isNaN(an) || isNaN(bn) ? a.localeCompare(b) : an - bn;
  });
  const pendingCount = tools.filter(t => t.status === 'pending').length;

  const stats = {
    total: filtered.filter(t => t.status !== 'retired').length,
    active: filtered.filter(t => t.status === 'active').length,
    tagged: filtered.filter(t => ['tagged_out', 'in_repair', 'in_transit'].includes(t.status)).length,
    value: filtered.filter(t => t.status !== 'retired').reduce((s, t) => s + (resolveToolValue(t) || 0), 0),
  };

  function openAdd() {
    setEditTool(null);
    setForm({ status: 'active', branch_id: isAdmin ? '' : profile?.branch_id });
    toolMsg.clearMessage();
    setShowModal(true);
  }

  function openEdit(t) {
    setEditTool(t);
    setForm({ name: t.name, model_number: t.model_number || '', serial_number: t.serial_number || '', description: t.description || '', cost: t.cost || '', branch_id: t.branch_id || '', department: t.department || '', station_truck: t.station_truck || '', status: t.status || 'active', acquisition_type: t.acquisition_type || 'unknown' });
    toolMsg.clearMessage();
    setShowModal(true);
  }

  function openTagout(t) {
    setTagoutTool(t);
    setTagForm({ reason: 'Damage', issue_description: '', shop_name: '', estimated_repair_cost: '', repair_status: 'tagged_out' });
    tagoutMsg.clearMessage();
    setShowTagout(true);
  }

  function openReceive(t) {
    setReceiveTool(t);
    setReceiveForm({ finalStatus: 'active', department: '', station_truck: '' });
    receiveMsg.clearMessage();
    setShowReceive(true);
  }

  async function openHistory(t) {
    setHistoryTool(t);
    setHistoryData([]);
    setShowHistory(true);
    setHistoryLoading(true);
    const { data } = await supabase.from('tool_history').select('*').eq('tool_id', t.id).order('created_at', { ascending: false });
    setHistoryData(data || []);
    setHistoryLoading(false);
  }

  function handleTagout(t) {
    if (isMobile) { setMobileTagoutTool(t); setShowMobileTagout(true); }
    else { openTagout(t); }
  }

  // ── OPEN RETROACTIVE REPAIR MODAL ──
  async function openRepairModal(t) {
    setShowModal(false);
    setRepairTool(t);
    setSelectedTagout(null);
    setRepairForm({});
    setRepairReceiptFile(null);
    setRepairReceiptPreview(null);
    repairMsg.clearMessage();
    setTagoutHistoryLoading(true);
    setShowRepairModal(true);
    const { data } = await supabase
      .from('tagout_log')
      .select('*, tools(name, model_number, serial_number, tool_models(msrp))')
      .eq('tool_id', t.id)
      .order('tag_date', { ascending: false });
    setTagoutHistory(data || []);
    setTagoutHistoryLoading(false);
  }

  function selectTagoutEvent(record) {
    setSelectedTagout(record);
    const isTraded = record.repair_or_replace === 'traded';
    const isReplaced = record.repair_or_replace === 'replaced';
    setRepairForm({
      returned_by_name: record.returned_by_name || profile?.full_name || '',
      ticket_number: record.ticket_number || '',
      actual_repair_cost: record.actual_repair_cost || '',
      is_warranty: record.is_warranty || false,
      repair_or_replace: record.repair_or_replace || 'repaired',
      replacement_msrp: record.replacement_msrp || '',
      resolution_notes: record.resolution_notes || '',
      new_tool_name: '',
      new_serial_number: record.tools?.serial_number || '',
      new_model_number: record.tools?.model_number || '',
    });
    setRepairReceiptFile(null);
    setRepairReceiptPreview(null);
    repairMsg.clearMessage();
  }

  function handleRepairReceiptChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setRepairReceiptFile(file);
    setRepairReceiptPreview(URL.createObjectURL(file));
  }

  function clearRepairReceipt() {
    setRepairReceiptFile(null);
    setRepairReceiptPreview(null);
    if (repairReceiptRef.current) repairReceiptRef.current.value = '';
  }

  async function saveRepairDetails() {
    if (!repairForm.resolution_notes?.trim()) { repairMsg.showError('Resolution notes are required.'); return; }
    const isReplaced = repairForm.repair_or_replace === 'replaced';
    const isTraded = repairForm.repair_or_replace === 'traded';
    if ((isReplaced || isTraded) && !repairForm.new_serial_number?.trim()) { repairMsg.showError('New serial number is required.'); return; }
    if ((isReplaced || isTraded) && !repairForm.new_model_number?.trim()) { repairMsg.showError('New model number is required.'); return; }
    if (isTraded && !repairForm.new_tool_name?.trim()) { repairMsg.showError('New tool name is required when the tool was traded.'); return; }
    if ((isReplaced || isTraded) && !repairForm.replacement_msrp) { repairMsg.showError('Replacement MSRP is required when the tool is replaced or traded.'); return; }

    setRepairSaving(true);

    let receiptPhotoPath = null;
    if (repairReceiptFile) {
      setRepairReceiptUploading(true);
      const { path, error } = await uploadPhoto(repairReceiptFile, 'receipts');
      setRepairReceiptUploading(false);
      if (error) { repairMsg.showError('Receipt photo upload failed: ' + error); setRepairSaving(false); return; }
      receiptPhotoPath = path;
    }

    await supabase.from('tagout_log').update({
      status: 'Returned',
      return_date: selectedTagout.return_date || new Date().toISOString(),
      returned_by_name: repairForm.returned_by_name,
      resolution_notes: repairForm.resolution_notes,
      actual_repair_cost: repairForm.is_warranty ? 0 : (repairForm.actual_repair_cost || null),
      repair_or_replace: repairForm.repair_or_replace,
      replacement_msrp: (isReplaced || isTraded) ? (repairForm.replacement_msrp || null) : null,
      is_warranty: repairForm.is_warranty || false,
      ticket_number: repairForm.ticket_number || null,
      ...(receiptPhotoPath && { receipt_photo_url: receiptPhotoPath }),
    }).eq('id', selectedTagout.id);

    if (isReplaced || isTraded) {
      const toolUpdate = {};
      if (repairForm.new_serial_number?.trim()) toolUpdate.serial_number = repairForm.new_serial_number.trim();
      if (repairForm.new_model_number?.trim()) toolUpdate.model_number = repairForm.new_model_number.trim();
      if (isTraded && repairForm.new_tool_name?.trim()) toolUpdate.name = repairForm.new_tool_name.trim();
      if (Object.keys(toolUpdate).length > 0) {
        await supabase.from('tools').update(toolUpdate).eq('id', repairTool.id);
      }
    }

    const outcomeLabel = isTraded ? 'Traded' : isReplaced ? 'Replaced' : 'Repaired';
    await logHistory(
      repairTool.id, 'edited', null, null,
      profile?.full_name, profile?.id,
      `Retroactive repair details added — ${outcomeLabel}: ${repairForm.resolution_notes}`,
      {
        repair_or_replace: repairForm.repair_or_replace,
        actual_repair_cost: repairForm.actual_repair_cost,
        ...(isTraded && { tool_name_updated: repairForm.new_tool_name }),
        ...((isReplaced || isTraded) ? { serial_number_updated: repairForm.new_serial_number, model_number_updated: repairForm.new_model_number } : {}),
      }
    );

    setRepairSaving(false);
    repairMsg.showSuccess('Repair details saved successfully.');
    setTimeout(() => { setShowRepairModal(false); load(); }, 1500);
  }

  const rrf = (k, v) => setRepairForm(p => ({ ...p, [k]: v }));

  async function saveTool() {
    if (!form.name?.trim()) { toolMsg.showError('Tool name is required.'); return; }
    setSaving(true);
    const isStatusLocked = editTool && ['tagged_out', 'in_repair', 'in_transit'].includes(editTool.status);
    const payload = {
      name: form.name.trim(), model_number: form.model_number || null,
      serial_number: form.serial_number || null, description: form.description || null,
      cost: form.cost || null, branch_id: form.branch_id || null,
      department: form.department || null, station_truck: form.station_truck || null,
      status: isStatusLocked ? editTool.status : (form.status || 'active'),
      is_spare: editTool ? editTool.is_spare : false,
      is_branch_spare: editTool ? editTool.is_branch_spare : false,
      acquisition_type: form.acquisition_type || 'unknown',
    };
    if (editTool) {
      const oldStatus = editTool.status;
      const newStatus = isStatusLocked ? editTool.status : (form.status || 'active');
      const { error: updateError } = await supabase.from('tools').update(payload).eq('id', editTool.id);
      if (updateError) { toolMsg.showError('Error saving tool: ' + updateError.message); setSaving(false); return; }
      if (oldStatus !== newStatus) {
        await logHistory(editTool.id, 'status_change', oldStatus, newStatus, profile?.full_name, profile?.id,
          `Status changed from ${STATUS_LABEL[oldStatus] || oldStatus} to ${STATUS_LABEL[newStatus] || newStatus}`);
      } else {
        await logHistory(editTool.id, 'edited', oldStatus, oldStatus, profile?.full_name, profile?.id, 'Tool record updated');
      }
    } else {
      const { data: inserted } = await supabase.from('tools').insert({ ...payload, entered_by: profile?.id }).select().single();
      if (inserted) {
        await logHistory(inserted.id, 'added', null, payload.status, profile?.full_name, profile?.id,
          `Added to inventory at ${branches.find(b => String(b.id) === String(payload.branch_id))?.name || 'unknown branch'}`);
        if (payload.status === 'pending') {
          const adminIds = await getAdminIds();
          await sendNotification(adminIds,
                `New tool pending receipt: ${payload.name}`,
                `${profile?.full_name} added ${payload.name} with status "Pending receipt". Confirm when it arrives.`,
                'pending_tool',
                'tools',
                'fleet'
              );
        }
      }
    }
    setSaving(false);
    setShowModal(false);
    load();
  }

  async function saveTagout() {
    if (!tagForm.issue_description?.trim()) { tagoutMsg.showError('Issue description is required.'); return; }
    setSaving(true);
    const toolId = parseInt(tagoutTool.id);
    const oldStatus = tagoutTool.status;
    const { error: toolError } = await supabase.from('tools').update({ status: tagForm.repair_status }).eq('id', toolId);
    if (toolError) { tagoutMsg.showError('Error updating tool status: ' + toolError.message); setSaving(false); return; }
    const { error: logError } = await supabase.from('tagout_log').insert({
      tool_id: toolId, reason: tagForm.reason, issue_description: tagForm.issue_description,
      tagged_by_name: profile?.full_name, status: tagForm.repair_status,
      shop_name: tagForm.shop_name || null, estimated_repair_cost: tagForm.estimated_repair_cost || null,
    });
    if (logError) { tagoutMsg.showError('Error saving tag-out record: ' + logError.message); setSaving(false); return; }
    await logHistory(toolId, 'tagged_out', oldStatus, tagForm.repair_status, profile?.full_name, profile?.id,
      `${tagForm.reason}: ${tagForm.issue_description}`,
      { shop_name: tagForm.shop_name, estimated_repair_cost: tagForm.estimated_repair_cost });
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Tool tagged out: ${tagoutTool.name}`,
            `${profile?.full_name} tagged out ${tagoutTool.name} at ${tagoutTool.branches?.name || 'unknown branch'}. Reason: ${tagForm.reason}.`,
            'tool_tagged_out',
            'tools',
            'tagout',
            toolId
          );
    const adminEmails = await getAdminEmails();
    const managerEmails = await getBranchManagerEmails(tagoutTool.branch_id);
    const allEmails = [...new Set([...adminEmails, ...managerEmails])];
    await sendEmail(allEmails, `Tool tagged out: ${tagoutTool.name}`,
      // #126 / M3, 78v - the trailing id is what makes the email's button open
      // THIS tag-out instead of the Fleet home screen. Station and serial stay
      // unset here exactly as before; only the id is new.
      emailToolTaggedOut(tagoutTool.name, tagoutTool.branches?.name || 'unknown branch', tagForm.reason, profile?.full_name, null, null, toolId));
    setSaving(false);
    setShowTagout(false);
    load();
  }

  async function confirmReceipt() {
    const isSpare = receiveForm.finalStatus === 'spare';
    if (!isSpare && !receiveForm.department?.trim()) { receiveMsg.showError('Department is required for active tools.'); return; }
    if (!isSpare && !receiveForm.station_truck?.trim()) { receiveMsg.showError('Station / truck is required for active tools.'); return; }
    setSaving(true);
    const payload = {
      status: isSpare ? 'spare' : 'active', is_spare: false, is_branch_spare: isSpare,
      department: isSpare ? null : receiveForm.department.trim(),
      station_truck: isSpare ? null : receiveForm.station_truck.trim(),
      received_at: new Date().toISOString(), received_by_name: profile?.full_name || '',
    };
    const { error } = await supabase.from('tools').update(payload).eq('id', receiveTool.id);
    if (error) { receiveMsg.showError('Error confirming receipt: ' + error.message); setSaving(false); return; }
    await logHistory(receiveTool.id, 'receipt_confirmed', 'pending', payload.status, profile?.full_name, profile?.id,
      isSpare ? 'Received and added to branch spare buffer' : `Received and assigned to ${receiveForm.department} — ${receiveForm.station_truck}`);
    const adminIds = await getAdminIds();
    await sendNotification(adminIds,
            `Receipt confirmed: ${receiveTool.name}`,
            `${profile?.full_name} confirmed receipt of ${receiveTool.name} and set it to ${isSpare ? 'branch spare' : `active (${receiveForm.department} — ${receiveForm.station_truck})`}.`,
            'receipt_confirmed',
            'tools',
            'fleet'
          );
    setSaving(false);
    setShowReceive(false);
    load();
  }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const tf = (k, v) => setTagForm(p => ({ ...p, [k]: v }));
  const rf = (k, v) => setReceiveForm(p => ({ ...p, [k]: v }));

  // The history rows are the part of the old handler that had to survive the
  // swap. What changed is that they can now tell the truth: the note used to
  // read "1 label printed · PT-E560BT 2x1" unconditionally, because that was the
  // only thing the button could do. It can now do six sizes across three
  // destinations, so the note reads what actually ran.
  async function handlePrintComplete(result) {
    const printedTools = selectedTools;
    if (printedTools.length === 0) return;

    const destinationLabel = DESTINATIONS[result?.destinationId]?.label || 'printer';
    const sizeLabel = LABEL_FORMATS[result?.formatId]?.label || result?.formatId || '—';
    // The verb is per destination and it is not decoration. A Zebra batch is
    // queued, not printed - print_jobs owns what happens to each job after
    // dispatch - and a Canon run produces a file rather than a label.
    const verb = { canon: 'exported', zebra: 'queued' }[result?.destinationId] || 'printed';

    await Promise.all(printedTools.map(tool => {
      const copies = Math.max(1, result?.copiesById?.[tool.id] ?? 1);
      return supabase.from('tool_history').insert({
        tool_id: tool.id,
        event_type: 'label_printed',
        changed_by_name: profile?.full_name || null,
        changed_by_id: profile?.id || null,
        notes: `${copies} label${copies === 1 ? '' : 's'} ${verb} · ${destinationLabel} ${sizeLabel}`,
      });
    }));

    toolbarMsg.showSuccess(`${result?.totalLabels ?? printedTools.length} label${result?.totalLabels === 1 ? '' : 's'} ${verb} · ${destinationLabel}.`);
  }

  function toggleSelect(id) {
    setSelectedIds(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  }
  function selectAllFiltered() { setSelectedIds(new Set(filtered.map(t => t.id))); }
  function selectPage() { setSelectedIds(prev => { const next = new Set(prev); paged.forEach(t => next.add(t.id)); return next; }); }
  function clearSelection() { setSelectedIds(new Set()); }
  function selectByDeptStation() {
    const dept = filterDept.length === 1 ? filterDept[0] : null;
    const station = filterStation.length === 1 ? filterStation[0] : null;
    if (!dept && !station) { toolbarMsg.showWarning('Filter to a specific department or station first, then use this option.'); return; }
    toolbarMsg.clearMessage();
    setSelectedIds(new Set(filtered.filter(t => (!dept || t.department === dept) && (!station || t.station_truck === station)).map(t => t.id)));
  }

  // Derived flags for repair modal
  const repairIsReplaced = repairForm.repair_or_replace === 'replaced';
  const repairIsTraded = repairForm.repair_or_replace === 'traded';
  const repairShowNewFields = repairIsReplaced || repairIsTraded;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Fleet inventory</div>
        <div className="page-sub">
          {isAdmin ? `All tools across ${companyName} locations`
            : profile?.department ? `${profile.department} department · ${profile.branch_name}`
            : `${profile?.branch_name || 'Your branch'} tools`}
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue"><div className="stat-label">Total tools</div><div className="stat-value">{stats.total}</div></div>
        <div className="stat-card green"><div className="stat-label">Active</div><div className="stat-value green">{stats.active}</div></div>
        <div className="stat-card amber"><div className="stat-label">Out of service</div><div className="stat-value amber">{stats.tagged}</div></div>
        <div className="stat-card"><div className="stat-label">Fleet value</div><div className="stat-value" style={{ fontSize: '18px' }}>{fmt$(stats.value)}</div></div>
      </div>

      {stats.tagged > 0 && (
        <div className="alert alert-warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          {stats.tagged} tool{stats.tagged !== 1 ? 's are' : ' is'} currently out of service.
        </div>
      )}

      {pendingCount > 0 && isManager && (
        <div className="alert alert-warning">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          {pendingCount} tool{pendingCount !== 1 ? 's are' : ' is'} awaiting receipt confirmation.
          <button className="btn btn-sm btn-ghost" style={{ marginLeft: '8px' }} onClick={() => { setFilterStatus(['pending']); setPage(1); }}>View pending</button>
        </div>
      )}

      <div className="toolbar">
        <input className="search-input" placeholder="Search name, model, serial, branch..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
        {isAdmin && (
          <MultiSelect label="All branches" options={branches.map(b => ({ value: String(b.id), label: b.name }))} selected={filterBranch} onChange={v => { setFilterBranch(v); setPage(1); }} />
        )}
        <MultiSelect label="All departments" options={depts.map(d => ({ value: d, label: d }))} selected={filterDept} onChange={v => { setFilterDept(v); setPage(1); }} />
        <MultiSelect label="All stations" options={stations.map(s => ({ value: s, label: `Station ${s}` }))} selected={filterStation} onChange={v => { setFilterStation(v); setPage(1); }} />
        <MultiSelect
          label="All statuses"
          options={[
            { value: 'active', label: 'Active' }, { value: 'pending', label: 'Pending receipt' },
            { value: 'tagged_out', label: 'Tagged out' }, { value: 'in_repair', label: 'In repair' },
            { value: 'in_transit', label: 'In transit' }, { value: 'retired', label: 'Retired' },
          ]}
          selected={filterStatus}
          onChange={v => { setFilterStatus(v); setPage(1); }}
        />
        {isManager && <button className="btn btn-primary" onClick={openAdd}>+ Add tool</button>}
      </div>

      {!isMobile && (isAdmin || isManager) && !isTeamMember && (
        <div style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button ref={tagoutBtnRef} className="btn btn-ghost btn-sm" onClick={selectPage}>Select page</button>
            <button className="btn btn-ghost btn-sm" onClick={selectAllFiltered}>Select all ({filtered.length})</button>
            <button className="btn btn-ghost btn-sm" onClick={selectByDeptStation}>Select dept/station</button>
            {selectedIds.size > 0 && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={clearSelection}>Clear ({selectedIds.size})</button>
                {labelPrintingEnabled !== false && (
                <button className="btn btn-primary btn-sm" onClick={() => setShowPrintModal(true)}>
                  🖨 Print {selectedIds.size} label{selectedIds.size !== 1 ? 's' : ''}
                </button>
                )}
              </>
            )}
          </div>
          <InlineMessage type={toolbarMsg.type} message={toolbarMsg.message} onDismiss={toolbarMsg.clearMessage} style={{ marginBottom: 0 }} />
        </div>
      )}

      <BatchPrintModal
        open={labelPrintingEnabled !== false && showPrintModal && selectedTools.length > 0}
        onClose={() => setShowPrintModal(false)}
        subjectType="tool"
        items={selectedTools}
        branchName={printBranchName}
        userId={profile?.id}
        onComplete={handlePrintComplete}
      />

      {/* ══ MOBILE CARD VIEW ══ */}
      {isMobile ? (
        <div>
          {loading ? <div className="loading"><div className="spinner" />Loading...</div>
            : paged.length === 0 ? (
              <div className="card"><div className="empty-state"><div className="empty-state-icon">🔧</div><div className="empty-state-title">No tools found</div><div className="empty-state-sub">Try adjusting your filters or add a tool.</div></div></div>
            ) : (
              <>
                <div className="tool-card-list">
                  {paged.map(t => (
                    <ToolCard key={t.id} tool={t} onTagout={handleTagout} onHistory={openHistory} isManager={isManager} isAdmin={isAdmin} isTeamMember={isTeamMember} />
                  ))}
                </div>
                <div className="pagination" style={{ marginTop: '12px' }}>
                  <span className="pagination-info">{filtered.length > 0 ? `${(page - 1) * PS + 1}–${Math.min(page * PS, filtered.length)} of ${filtered.length}` : '0 results'}</span>
                  <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
                  <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
                </div>
              </>
            )}
        </div>
      ) : (
        /* ══ DESKTOP TABLE VIEW ══ */
        <div className="card">
          <div className="table-wrap">
            {loading ? <div className="loading"><div className="spinner" />Loading...</div> : (
              <table>
                <thead>
                  <tr>
                    {(isAdmin || isManager) && !isTeamMember && <th style={{ width: '32px' }}><input type="checkbox" onChange={e => e.target.checked ? selectPage() : clearSelection()} checked={paged.length > 0 && paged.every(t => selectedIds.has(t.id))} style={{ accentColor: 'var(--green-dk)' }} /></th>}
                    <th>Tool name</th><th>Model #</th><th>Serial #</th><th>Branch</th><th>Dept</th><th>Station / truck</th><th ref={tagoutBtnRef}>MSRP</th><th>Cost</th><th>Status</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr><td colSpan="9"><div className="empty-state"><div className="empty-state-icon">🔧</div><div className="empty-state-title">No tools found</div><div className="empty-state-sub">Try adjusting your filters or add a tool.</div></div></td></tr>
                  ) : paged.map(t => (
                    <tr key={t.id} className={['tagged_out', 'in_transit', 'pending'].includes(t.status) ? 'flagged' : ''}>
                      {(isAdmin || isManager) && !isTeamMember && <td><input type="checkbox" checked={selectedIds.has(t.id)} onChange={() => toggleSelect(t.id)} style={{ accentColor: 'var(--green-dk)' }} /></td>}
                      <td><button className="btn btn-ghost btn-sm" style={{ fontWeight: 700, padding: '0', textAlign: 'left' }} onClick={() => openHistory(t)}>{t.name}</button></td>
                      <td className="text-muted">{t.model_number || '—'}</td>
                      <td className="text-muted text-sm">{t.serial_number || '—'}</td>
                      <td>{t.branches?.name || '—'}</td>
                      <td>{t.department || '—'}</td>
                      <td>{t.station_truck || '—'}</td>
                      <td>{t.tool_models?.msrp ? fmt$(t.tool_models.msrp) : '—'}</td>
                      <td>{fmt$(t.cost)}</td>
                      <td><span className={`badge ${STATUS_BADGE[t.status] || 'badge-active'}`}>{STATUS_LABEL[t.status] || t.status}</span></td>
                      <td>
                        <div className="flex gap-1">
                          {isManager && t.status !== 'pending' && <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>Edit</button>}
                          <LabelCard
                            mode="tool"
                            variant="iconTrigger"
                            isMobile={false}
                            item={t}
                            branchName={t.branches?.name || branches.find(b => String(b.id) === String(t.branch_id))?.name || ''}
                          />
                          {t.status === 'active' && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleTagout(t)}
                            >Tag out</button>
              )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          <div className="pagination">
            <span className="pagination-info">{filtered.length > 0 ? `Showing ${(page - 1) * PS + 1}–${Math.min(page * PS, filtered.length)} of ${filtered.length} tools` : '0 results'}</span>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}

      {/* ── HISTORY MODAL ── */}
      {showHistory && historyTool && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowHistory(false)}>
          <div className="modal" style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <div>
                <span className="modal-title">Tool history</span>
                <div className="text-sm text-muted" style={{ marginTop: '2px' }}>{historyTool.name}{historyTool.serial_number ? ` · S/N: ${historyTool.serial_number}` : ''}</div>
              </div>
              <button className="close-btn" onClick={() => setShowHistory(false)}>×</button>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--gray-mid)', flexWrap: 'wrap' }}>
              <span>Branch: <strong style={{ color: 'var(--black)' }}>{historyTool.branches?.name || '—'}</strong></span>
              <span>Dept: <strong style={{ color: 'var(--black)' }}>{historyTool.department || '—'}</strong></span>
              <span>Station: <strong style={{ color: 'var(--black)' }}>{historyTool.station_truck || '—'}</strong></span>
              <span>Status: <span className={`badge ${STATUS_BADGE[historyTool.status] || 'badge-active'}`} style={{ fontSize: '11px' }}>{STATUS_LABEL[historyTool.status] || historyTool.status}</span></span>
            </div>
            <div className="modal-body" style={{ maxHeight: '480px', overflowY: 'auto' }}>
              {historyLoading ? <div className="loading"><div className="spinner" />Loading history...</div>
                : historyData.length === 0 ? (
                  <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No history yet</div><div className="empty-state-sub">History is recorded from this point forward.</div></div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    {historyData.map((h, i) => (
                      <div key={h.id} style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                          <div style={{ width: '10px', height: '10px', borderRadius: '50%', marginTop: '4px', flexShrink: 0, background: h.event_type === 'tagged_out' ? '#C62828' : h.event_type === 'receipt_confirmed' ? '#2E7D32' : h.event_type === 'added' ? '#1565C0' : '#757575' }} />
                          {i < historyData.length - 1 && <div style={{ width: '2px', flex: 1, background: '#E0E0E0', marginTop: '4px' }} />}
                        </div>
                        <div style={{ flex: 1, paddingBottom: '4px' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--black)' }}>{EVENT_LABEL[h.event_type] || h.event_type}</div>
                          {(h.old_status || h.new_status) && h.old_status !== h.new_status && (
                            <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '2px' }}>
                              {h.old_status && <span className={`badge ${STATUS_BADGE[h.old_status] || 'badge-active'}`} style={{ fontSize: '10px' }}>{STATUS_LABEL[h.old_status] || h.old_status}</span>}
                              {h.old_status && h.new_status && <span style={{ margin: '0 4px' }}>→</span>}
                              {h.new_status && <span className={`badge ${STATUS_BADGE[h.new_status] || 'badge-active'}`} style={{ fontSize: '10px' }}>{STATUS_LABEL[h.new_status] || h.new_status}</span>}
                            </div>
                          )}
                          {h.notes && <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '3px', fontStyle: 'italic' }}>"{h.notes}"</div>}
                          <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginTop: '4px' }}>{fmtDate(h.created_at)}{h.changed_by_name ? ` · ${h.changed_by_name}` : ''}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
            <div className="modal-footer"><button className="btn btn-ghost" onClick={() => setShowHistory(false)}>Close</button></div>
          </div>
        </div>
      )}

      {/* ── CONFIRM RECEIPT MODAL ── */}
      {showReceive && receiveTool && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowReceive(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title" style={{ color: '#2E7D32' }}>Confirm receipt</span>
              <button className="close-btn" onClick={() => setShowReceive(false)}>×</button>
            </div>
            <div style={{ background: '#F1F8E9', padding: '0.75rem 1.25rem', borderBottom: '1px solid #C5E1A5' }}>
              <strong>{receiveTool.name}</strong>
              <div className="text-sm text-muted">{[receiveTool.model_number ? 'Model: ' + receiveTool.model_number : null, receiveTool.serial_number ? 'S/N: ' + receiveTool.serial_number : null].filter(Boolean).join(' · ') || 'No model/serial on record'}</div>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>Confirmed by: <strong>{profile?.full_name}</strong></div>
            <div className="modal-body">
              <InlineMessage type={receiveMsg.type} message={receiveMsg.message} onDismiss={receiveMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group full">
                  <label className="form-label">Set tool status *</label>
                  <select className="form-select" value={receiveForm.finalStatus} onChange={e => rf('finalStatus', e.target.value)}>
                    <option value="active">Active — assign to fleet (department + station required)</option>
                    <option value="spare">Branch spare — add to local spare buffer</option>
                  </select>
                </div>
                {receiveForm.finalStatus === 'active' && (
                  <>
                    <div className="form-group"><label className="form-label">Department *</label><input className="form-input" value={receiveForm.department} onChange={e => rf('department', e.target.value)} placeholder="e.g. Install, Fabrication" /></div>
                    <div className="form-group"><label className="form-label">Station / truck *</label><input className="form-input" value={receiveForm.station_truck} onChange={e => rf('station_truck', e.target.value)} placeholder="e.g. Truck 4, Station 2" /></div>
                  </>
                )}
                {receiveForm.finalStatus === 'spare' && (
                  <div className="form-group full" style={{ color: 'var(--gray-lt)', fontSize: '13px', padding: '0.5rem 0' }}>This tool will be added to your branch's local spare buffer. It can be assigned to fleet later when needed.</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowReceive(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={confirmReceipt} disabled={saving}>{saving ? 'Saving...' : 'Confirm receipt'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD / EDIT MODAL ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editTool ? 'Edit tool' : 'Add tool'}</span>
              <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <InlineMessage type={toolMsg.type} message={toolMsg.message} onDismiss={toolMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Tool name *</label><input className="form-input" value={form.name || ''} onChange={e => f('name', e.target.value)} placeholder="e.g. Cordless Drill" /></div>
                <div className="form-group">
                  <label className="form-label">Model number{editTool && !isAdmin && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>(admin only)</span>}</label>
                  <input className="form-input" value={form.model_number || ''} onChange={e => f('model_number', e.target.value)} placeholder="e.g. DCD771C2" readOnly={!!(editTool && !isAdmin)} style={editTool && !isAdmin ? { background: 'var(--off-white)', color: 'var(--gray-lt)' } : {}} />
                </div>
                <div className="form-group">
                  <label className="form-label">Serial number{editTool && !isAdmin && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>(admin only)</span>}</label>
                  <input className="form-input" value={form.serial_number || ''} onChange={e => f('serial_number', e.target.value)} readOnly={!!(editTool && !isAdmin)} style={editTool && !isAdmin ? { background: 'var(--off-white)', color: 'var(--gray-lt)' } : {}} />
                </div>
                <div className="form-group"><label className="form-label">Cost ($)</label><input className="form-input" type="number" min="0" step="0.01" value={form.cost || ''} onChange={e => f('cost', e.target.value)} /></div>
                <div className="form-group">
                  <label className="form-label">How was this acquired? <span style={{ fontSize: '11px', color: 'var(--gray-lt)', fontWeight: 400 }}>affects savings reporting</span></label>
                  <select className="form-select" value={form.acquisition_type || 'unknown'} onChange={e => f('acquisition_type', e.target.value)}>
                    {ACQUISITION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                {isAdmin && <div className="form-group"><label className="form-label">Branch</label><select className="form-select" value={form.branch_id || ''} onChange={e => f('branch_id', e.target.value)}><option value="">Select branch</option>{branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>}
                <div className="form-group"><label className="form-label">Department</label><input className="form-input" value={form.department || ''} onChange={e => f('department', e.target.value)} placeholder="e.g. Install, Fabrication" /></div>
                <div className="form-group"><label className="form-label">Station / truck</label><input className="form-input" value={form.station_truck || ''} onChange={e => f('station_truck', e.target.value)} placeholder="e.g. Truck 4" /></div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  {editTool && ['tagged_out', 'in_repair', 'in_transit'].includes(editTool.status) ? (
                    <div style={{ fontSize: '13px', color: 'var(--gray-mid)', padding: '8px 0' }}>
                      <span className={`badge ${STATUS_BADGE[editTool.status]}`}>{STATUS_LABEL[editTool.status]}</span>
                      <span style={{ marginLeft: '8px' }}>Use Tag-out log → Return to service to change this status.</span>
                    </div>
                  ) : (
                    <select className="form-select" value={form.status || 'active'} onChange={e => f('status', e.target.value)}>
                      <option value="active">Active</option>
                      <option value="pending">Pending receipt</option>
                      <option value="retired">Retired</option>
                    </select>
                  )}
                </div>
                <div className="form-group full"><label className="form-label">Description / notes</label><textarea className="form-textarea" value={form.description || ''} onChange={e => f('description', e.target.value)} /></div>
              </div>

              {/* ── RETROACTIVE REPAIR DETAILS — admin only ── */}
              {isAdmin && editTool && (
                <div style={{ marginTop: '1.25rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                    Repair history
                  </div>
                  <button
                    className="btn btn-ghost"
                    style={{ width: '100%', justifyContent: 'center', fontSize: '13px' }}
                    onClick={() => openRepairModal(editTool)}
                  >
                    📋 Log retroactive repair details
                  </button>
                  <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginTop: '6px', textAlign: 'center' }}>
                    Admin only — attach cost, outcome, and receipt to a past tag-out event
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTool} disabled={saving}>{saving ? 'Saving...' : 'Save tool'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DESKTOP TAGOUT MODAL ── */}
      {showTagout && tagoutTool && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowTagout(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title" style={{ color: '#C62828' }}>Tag out tool</span>
              <button className="close-btn" onClick={() => setShowTagout(false)}>×</button>
            </div>
            <div style={{ background: '#FFF8E8', padding: '0.75rem 1.25rem', borderBottom: '1px solid #FFE0A0' }}>
              <strong>{tagoutTool.name}</strong>
              <div className="text-sm text-muted">{[tagoutTool.branches?.name, tagoutTool.department, tagoutTool.station_truck, tagoutTool.serial_number ? 'S/N: ' + tagoutTool.serial_number : null].filter(Boolean).join(' · ')}</div>
            </div>
            <div style={{ background: '#F8F8F8', padding: '0.75rem 1.25rem', borderBottom: '1px solid var(--border)', fontSize: '13px' }}>Tagged out by: <strong>{profile?.full_name}</strong></div>
            <div className="modal-body">
              <InlineMessage type={tagoutMsg.type} message={tagoutMsg.message} onDismiss={tagoutMsg.clearMessage} />
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Reason *</label><select className="form-select" value={tagForm.reason} onChange={e => tf('reason', e.target.value)}><option>Damage</option><option>Malfunction</option><option>Scheduled maintenance</option><option>Failed inspection</option><option>Missing parts</option><option>Safety concern</option><option>Other</option></select></div>
                <div className="form-group"><label className="form-label">Initial status</label><select className="form-select" value={tagForm.repair_status} onChange={e => tf('repair_status', e.target.value)}><option value="tagged_out">Tagged out — awaiting assessment</option><option value="in_repair">In repair — sent to shop</option></select></div>
                <div className="form-group"><label className="form-label">Repair shop (if known)</label><input className="form-input" value={tagForm.shop_name} onChange={e => tf('shop_name', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Est. repair cost ($)</label><input className="form-input" type="number" min="0" step="0.01" value={tagForm.estimated_repair_cost} onChange={e => tf('estimated_repair_cost', e.target.value)} /></div>
                <div className="form-group full"><label className="form-label">Issue description *</label><textarea className="form-textarea" value={tagForm.issue_description} onChange={e => tf('issue_description', e.target.value)} placeholder="Describe the issue requiring tag-out..." /></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowTagout(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={saveTagout} disabled={saving}>{saving ? 'Saving...' : 'Tag out tool'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE TAGOUT FLOW ── */}
      {showMobileTagout && mobileTagoutTool && (
        <MobileTagout tool={mobileTagoutTool} onClose={() => setShowMobileTagout(false)} onSuccess={() => { setShowMobileTagout(false); load(); }} />
      )}

      {/* ── RETROACTIVE REPAIR DETAILS MODAL ── */}
      {showRepairModal && repairTool && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowRepairModal(false)}>
          <div className="modal" style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <div>
                <span className="modal-title">Log repair details</span>
                <div className="text-sm text-muted" style={{ marginTop: '2px' }}>{repairTool.name} · Admin retroactive entry</div>
              </div>
              <button className="close-btn" onClick={() => setShowRepairModal(false)}>×</button>
            </div>

            <div style={{ background: '#F0F4FF', padding: '0.75rem 1.25rem', borderBottom: '1px solid #C7D2FE', fontSize: '12px', color: '#3730A3' }}>
              Select a past tag-out event to attach repair details to. This will update that record with cost, outcome, and receipt information.
            </div>

            <div className="modal-body">
              <InlineMessage type={repairMsg.type} message={repairMsg.message} onDismiss={repairMsg.clearMessage} />

              {/* ── STEP 1: SELECT TAGOUT EVENT ── */}
              {tagoutHistoryLoading ? (
                <div className="loading"><div className="spinner" />Loading tag-out history...</div>
              ) : tagoutHistory.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📋</div>
                  <div className="empty-state-title">No tag-out history</div>
                  <div className="empty-state-sub">This tool has no tag-out records. Log a tag-out first before adding repair details.</div>
                </div>
              ) : (
                <>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>
                    Select tag-out event
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
                    {tagoutHistory.map(record => {
                      const isSelected = selectedTagout?.id === record.id;
                      return (
                        <button
                          key={record.id}
                          onClick={() => selectTagoutEvent(record)}
                          style={{
                            padding: '12px 14px', borderRadius: '8px', textAlign: 'left', cursor: 'pointer',
                            border: `2px solid ${isSelected ? 'var(--green-dk)' : 'var(--border)'}`,
                            background: isSelected ? '#F1FFF3' : 'white',
                            transition: 'all 0.15s',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div>
                              <div style={{ fontWeight: 600, fontSize: '13px', color: isSelected ? 'var(--green-dk)' : 'var(--black)' }}>
                                {record.reason} — {record.tag_date ? new Date(record.tag_date).toLocaleDateString() : '—'}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--gray-mid)', marginTop: '2px' }}>
                                Tagged by: {record.tagged_by_name || '—'}
                                {record.shop_name ? ` · Shop: ${record.shop_name}` : ''}
                              </div>
                              {record.issue_description && (
                                <div style={{ fontSize: '11px', color: 'var(--gray-lt)', marginTop: '2px', fontStyle: 'italic' }}>"{record.issue_description}"</div>
                              )}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              {record.status === 'Returned' ? (
                                <span className="badge badge-active" style={{ fontSize: '11px' }}>Returned</span>
                              ) : (
                                <span className="badge badge-tagged" style={{ fontSize: '11px' }}>{record.status}</span>
                              )}
                              {record.repair_or_replace && (
                                <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '4px', textAlign: 'right' }}>
                                  {record.repair_or_replace}
                                </div>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* ── STEP 2: REPAIR DETAILS FORM (shown after selecting) ── */}
                  {selectedTagout && (
                    <>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '12px', paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                        Repair details
                      </div>
                      <div className="form-grid">
                        <div className="form-group">
                          <label className="form-label">Returned by</label>
                          <input className="form-input" value={repairForm.returned_by_name || ''} onChange={e => rrf('returned_by_name', e.target.value)} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Shop ticket # <span style={{ fontSize: '11px', color: 'var(--gray-lt)' }}>(optional)</span></label>
                          <input className="form-input" value={repairForm.ticket_number || ''} onChange={e => rrf('ticket_number', e.target.value)} placeholder="e.g. INV-2045" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Actual cost ($)</label>
                          <input className="form-input" type="number" min="0" step="0.01"
                            value={repairForm.is_warranty ? '0' : (repairForm.actual_repair_cost || '')}
                            onChange={e => rrf('actual_repair_cost', e.target.value)}
                            disabled={repairForm.is_warranty}
                            style={repairForm.is_warranty ? { background: 'var(--off-white)', color: 'var(--gray-lt)' } : {}}
                          />
                        </div>
                        <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '2px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px' }}>
                            <input type="checkbox" checked={repairForm.is_warranty || false} onChange={e => rrf('is_warranty', e.target.checked)} />
                            No cost — warranty claim
                          </label>
                        </div>
                        <div className="form-group">
                          <label className="form-label">Outcome</label>
                          <select className="form-select" value={repairForm.repair_or_replace || 'repaired'} onChange={e => rrf('repair_or_replace', e.target.value)}>
                            <option value="repaired">Repaired</option>
                            <option value="replaced">Replaced — same tool, new unit</option>
                            <option value="traded">Traded / swapped — different tool received</option>
                          </select>
                        </div>
                        {repairShowNewFields && (
                          <div className="form-group">
                            <label className="form-label">
                              Replacement MSRP ($)
                              {repairIsTraded && <span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Retail price of new tool received</span>}
                            </label>
                            <input className="form-input" type="number" min="0" step="0.01" value={repairForm.replacement_msrp || ''} onChange={e => rrf('replacement_msrp', e.target.value)} placeholder="What would this cost to buy new?" />
                          </div>
                        )}
                        <div className="form-group full">
                          <label className="form-label">Resolution notes *</label>
                          <textarea className="form-textarea" value={repairForm.resolution_notes || ''} onChange={e => rrf('resolution_notes', e.target.value)} placeholder={repairIsTraded ? 'Describe the trade — what was sent in, what was received, and the credit applied...' : 'What was repaired or replaced?'} />
                        </div>

                        {repairShowNewFields && (
                          <>
                            <div className="form-group full" style={{ marginBottom: '2px' }}>
                              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                                {repairIsTraded ? 'New tool details *' : 'Replacement unit details'} <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>{repairIsTraded ? '(updates tool record)' : '(optional)'}</span>
                              </div>
                            </div>
                            {repairIsTraded && (
                              <div className="form-group full">
                                <label className="form-label">New tool name *<span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {repairTool.name}</span></label>
                                <input className="form-input" value={repairForm.new_tool_name || ''} onChange={e => rrf('new_tool_name', e.target.value)} placeholder="e.g. Nail Gun" />
                              </div>
                            )}
                            <div className="form-group">
                              <label className="form-label">New serial number *<span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {repairTool.serial_number || '—'}</span></label>
                              <input className="form-input" value={repairForm.new_serial_number || ''} onChange={e => rrf('new_serial_number', e.target.value)} placeholder="Required" />
                            </div>
                            <div className="form-group">
                              <label className="form-label">New model number *<span style={{ fontSize: '11px', color: 'var(--gray-lt)', marginLeft: '6px' }}>Current: {repairTool.model_number || '—'}</span></label>
                              <input className="form-input" value={repairForm.new_model_number || ''} onChange={e => rrf('new_model_number', e.target.value)} placeholder="Required" />
                            </div>
                          </>
                        )}

                        {/* Receipt photo */}
                        <div className="form-group full" style={{ marginBottom: '2px' }}>
                          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--gray-mid)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '8px', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                            Receipt / invoice photo <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                          </div>
                        </div>
                        <div className="form-group full">
                          {!repairReceiptPreview ? (
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                                📷 Take photo
                                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleRepairReceiptChange} />
                              </label>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '6px', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '13px', fontWeight: 500, background: 'var(--off-white)', color: 'var(--black)' }}>
                                📁 Choose file
                                <input ref={repairReceiptRef} type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handleRepairReceiptChange} />
                              </label>
                            </div>
                          ) : (
                            <div style={{ position: 'relative', display: 'inline-block' }}>
                              <img src={repairReceiptPreview} alt="Receipt preview" style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: '8px', objectFit: 'cover', border: '1px solid var(--border)', display: 'block' }} />
                              <button onClick={clearRepairReceipt} style={{ position: 'absolute', top: '6px', right: '6px', background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                              <div style={{ fontSize: '11px', color: 'var(--gray-mid)', marginTop: '4px' }}>{repairReceiptFile?.name}</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            {selectedTagout && (
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setShowRepairModal(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveRepairDetails} disabled={repairSaving || repairReceiptUploading}>
                  {repairReceiptUploading ? 'Uploading photo...' : repairSaving ? 'Saving...' : 'Save repair details'}
                </button>
              </div>
            )}
            {!selectedTagout && tagoutHistory.length > 0 && (
              <div className="modal-footer">
                <button className="btn btn-ghost" onClick={() => setShowRepairModal(false)}>Cancel</button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ── ONBOARDING CALLOUTS ── */}
{!isMobile && (
  <>
    <OnboardingCallout
      calloutKey="fleet_tagout"
      text="Use Tag Out to remove a damaged or malfunctioning tool from service. You'll be prompted to describe the issue and send it for repair."
      anchorRef={tagoutBtnRef}
      show={paged.some(t => t.status === 'active')}
      onDismiss={() => setTagoutCalloutDismissed(true)}
    />
    <OnboardingCallout
      calloutKey="fleet_export_csv"
      text="Export a CSV of QR label data for your tools. Use this with the label printer to generate scannable tags for each tool."
      anchorRef={exportCsvBtnRef}
      show={tagoutCalloutDismissed}
    />
  </>
)}
{isMobile && (
  <OnboardingCallout
    calloutKey="fleet_tagout"
    text="Use Tag Out to remove a damaged or malfunctioning tool from service. You'll be prompted to describe the issue and send it for repair."
    anchorRef={null}
    isMobile={true}
    show={paged.some(t => t.status === 'active')}
  />
)}
    </div>
  );
}